import { useState, type ReactNode } from 'react';
import { useSearchParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckCircle } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { useDarkMode } from '@/hooks/useDarkMode';
import DarkModeToggle from '@/components/common/DarkModeToggle';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import SelectedPlansSummary from '@/components/checkout/SelectedPlansSummary';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import { getPlanShortName } from '@/consts/pricingPublic';
import { filterUnownedPlans, getAllOwnedMessage, parseOptionalParam, parsePlanParams, startCheckoutChain } from '@/services/checkoutChain';
import { showErrorToast, TOAST_MESSAGES } from '@/utils/toasts';
import { APIRequestError } from '@/services/api';
import type { SubscriptionPriceProduct, SubscriptionType, User } from '@/models';

/**
 * Register-Link-Handling für BEREITS EINGELOGGTE User.
 *
 * Marketing-Links wie `/auth/register?plan=X&coupon=Y` (oder mit mehreren Paketen
 * `?plan=X&plan=Z`) zeigen ausgeloggten Usern die volle Registrierung (AuthRouter).
 * Eingeloggte User landeten bisher auf einer 404 (AppRouter hat keine `/auth/register`-Route).
 * Diese Seite behandelt die drei Fälle:
 *   1. Kein/ungültiges Paket             -> Weiterleitung zu `/pricing`
 *   2. Alle Pakete bereits aktiv         -> Glückwunsch-Ansicht ("Du hast X bereits")
 *   3. Mind. ein Paket noch nicht gebucht -> Register-ähnliche Seite ohne Account-Felder
 *                                           mit "Jetzt buchen" -> (verketteter) Stripe-Checkout
 *                                           nur für die noch fehlenden Pakete
 */
export default function RegisterCheckoutPage() {
  const { user, loading: userLoading } = useUser();
  const { products, loading: productsLoading } = useSubscriptionProducts();
  const { isDarkMode } = useDarkMode();
  const [searchParams] = useSearchParams();

  const loading = userLoading || productsLoading;

  const plans = parsePlanParams(searchParams, products);
  const coupon = parseOptionalParam(searchParams, 'coupon');
  const referrerCode = parseOptionalParam(searchParams, 'referrer');

  // Auf den User-Context warten (analog CheckoutLauncher)
  if (loading) {
    return <LoadingSpinner message="Einen Moment..." />;
  }

  // Fall 1: kein gültiges Paket -> Preisseite
  if (plans.length === 0) {
    return <Navigate to="/pricing" replace />;
  }

  // Defensiv: AppRouter wird nur eingeloggt gerendert, user sollte also gesetzt sein
  if (user === null) {
    return <Navigate to="/login" replace />;
  }

  // Ownership auf Produktfamilien-Ebene: SKU ODER Produktfamilie aktiv (siehe checkoutChain.isPlanOwned).
  // Bereits besessene Pakete werden übersprungen, gebucht werden nur die restlichen.
  const remainingPlans = filterUnownedPlans(user, plans, products);

  return (
    <PageShell isDarkMode={isDarkMode}>
      {remainingPlans.length === 0 ? (
        <AlreadyOwnedView plans={plans} products={products} />
      ) : (
        <BookingConfirmView user={user} plans={remainingPlans} coupon={coupon} referrerCode={referrerCode} products={products} />
      )}
    </PageShell>
  );
}

interface PageShellProps {
  isDarkMode: boolean;
  children: ReactNode;
}

/** Voll-Screen-Rahmen, spiegelt die Registrierungsseite (Registration.tsx). */
function PageShell({ isDarkMode, children }: PageShellProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 px-4 py-8 relative">
      <DarkModeToggle className="absolute top-4 right-4" />

      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 space-y-6">
          {/* Logo */}
          <div className="text-center mb-2">
            <img
              src={isDarkMode ? '/logo_light.png' : '/logo.png'}
              alt="Börsencampus Logo"
              className="h-16 object-contain mx-auto my-4"
            />
            <div className="w-20 h-1 bg-blue-600 mx-auto rounded-full mb-6"></div>
          </div>

          {children}

          {/* Footer */}
          <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Powered by SvenBolz Trading Academy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AlreadyOwnedViewProps {
  plans: SubscriptionType[];
  products: SubscriptionPriceProduct[];
}

/** Fall 2: der User besitzt alle Pakete des Links bereits. */
function AlreadyOwnedView({ plans, products }: AlreadyOwnedViewProps) {
  // Mehrzeilige Namen (Community) auf die erste Zeile kürzen – dreizeilig in Anführungszeichen wäre unleserlich.
  const joinedNames = plans.map(plan => `„${getPlanShortName(products, plan)}“`).join(' und ');
  return (
    <div className="text-center space-y-4">
      <FontAwesomeIcon icon={faCheckCircle} className="w-14 h-14 text-green-500 mx-auto" />
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        Du hast {joinedNames} bereits!
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {plans.length > 1 ? 'Diese Pakete sind' : 'Dieses Paket ist'} in deinem Account bereits aktiv. Viel Erfolg beim Umsetzen!
      </p>
      <Link
        to="/dashboard"
        className="inline-flex items-center justify-center w-full py-3 px-4 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
      >
        Zum Dashboard
      </Link>
    </div>
  );
}

interface BookingConfirmViewProps {
  user: User;
  plans: SubscriptionType[];
  coupon: string | undefined;
  referrerCode: string | undefined;
  products: SubscriptionPriceProduct[];
}

/** Fall 3: register-ähnliche Seite ohne Account-Felder, "Jetzt buchen" -> Checkout. */
function BookingConfirmView({ user, plans, coupon, referrerCode, products }: BookingConfirmViewProps) {
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleBook = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await startCheckoutChain({
        user: user,
        plans: plans,
        coupon: coupon,
        referrerCode: referrerCode,
        source: 'register-link-loggedin',
        products: products,
      });
      if (result === 'ALL_OWNED') {
        // Zwischenzeitlich (z.B. in einem anderen Tab) gekauft
        setSubmitting(false);
        showErrorToast(getAllOwnedMessage(plans.length));
        navigate('/dashboard', { replace: true });
      }
      // REDIRECTING -> Browser wird zu Stripe weitergeleitet; Spinner absichtlich stehen lassen.
    } catch (error) {
      console.error('Checkout via register link failed:', error);
      setSubmitting(false);
      // Backend kann kein Stripe-Produkt auflösen (z.B. nicht konfiguriert) -> generischer
      // INTERNAL_ERROR, daher Message-Check (analog CheckoutLauncher).
      if (error instanceof APIRequestError && error.message.toLowerCase().includes('stripe product')) {
        showErrorToast('Dieses Paket ist aktuell nicht verfügbar.');
        navigate('/pricing', { replace: true });
        return;
      }
      showErrorToast(TOAST_MESSAGES.GENERAL_ERROR);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center">
        {plans.length > 1 ? 'Deine Pakete' : 'Dein Paket'}
      </h2>

      <SelectedPlansSummary plans={plans} couponApplied={coupon !== undefined} />

      {coupon !== undefined && (
        <p className="text-center text-sm text-gray-600 dark:text-gray-300">
          Gutscheincode <span className="font-medium">{coupon}</span> wird im Checkout angewendet.
        </p>
      )}

      <button
        type="button"
        onClick={handleBook}
        disabled={submitting}
        className="w-full flex justify-center items-center py-3 px-4 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
      >
        {submitting ? (
          <>
            <FontAwesomeIcon icon={faSpinner} className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
            Weiterleitung zum Checkout...
          </>
        ) : (
          'Jetzt buchen'
        )}
      </button>

      <div className="text-center">
        <Link
          to="/pricing"
          className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-medium"
        >
          Andere Pakete ansehen
        </Link>
      </div>
    </div>
  );
}
